/** Instrument screen for the mobile layout: price, chart, your position, market stats, Buy / Sell. */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { D, fmt, fmtCompactUsd, fmtPct, fmtPrice, fmtSigned, fmtUsdSigned, nextMarketOpen, pairLabel, type Side } from '@platform/shared';
import { ChartPanel } from '../../components/ChartPanel';
import { InstrumentIcon } from '../../components/InstrumentIcon';
import { usePriceMove } from '../../components/PriceMove';
import { PositionCard } from '../../components/mobile/Activity';
import { rememberSymbol, roundCls } from '../../components/mobile/chrome';
import { OrderTicket } from '../../components/mobile/OrderTicket';
import { MarginWarning, PriceFeedBanner, TradingStatusBanner } from '../../components/TradeNotices';
import { cx, Icon, isFall, MoveArrow, showsVolume, sideLabel, unitLabel } from '../../components/ui';
import { Link } from '../../lib/router';
import { openAlertBuilder } from '../../stores/alerts';
import { useCryptoMarket } from '../../stores/cryptoMarket';
import { toggleSearch } from '../../stores/search';
import { toggleFavorite, useFavorites } from '../../stores/favorites';
import { useInstrument, useQuote, useTicker } from '../../stores/market';
import { usePositions } from '../../stores/trading';
import { useManagedPositions } from '../../stores/managed';

function Stat({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[14px] text-muted">{label}</dt>
      <dd className={cx('tnum m-0 mt-0.5 truncate text-[16px] font-semibold', className)}>{children}</dd>
    </div>
  );
}

/** "14:30" today, otherwise "Mon 14:30", in the viewer's local time. */
function fmtOpens(d: Date) {
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? time : `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
}

export function MobileTrade({ symbol }: { symbol: string }) {
  const inst = useInstrument(symbol);
  const ticker = useTicker(symbol);
  const market = useCryptoMarket(symbol);
  const quote = useQuote(symbol);
  const move = usePriceMove(symbol);
  const all = usePositions();
  const managed = useManagedPositions();
  // The client's own positions first, then the ones our dealing team runs on their managed account.
  const positions = useMemo(() => [...all, ...managed].filter((p) => p.symbol === symbol), [all, managed, symbol]);
  const favorites = useFavorites();
  const [ticket, setTicket] = useState<Side | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => rememberSymbol(symbol), [symbol]);
  useEffect(() => setTicket(null), [symbol]);
  // The pinned top bar gets a hairline once the page scrolls under it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const closeTicket = useCallback(() => setTicket(null), []);

  const dp = inst?.priceDecimals ?? 2;

  const ccy = inst?.priceCcy ?? 'USD';
  const fav = favorites.includes(symbol);
  const open = ticker ? ticker.open : true;
  const always = !!inst && 'always' in inst.tradingHours;
  const opensAt = inst && !open ? nextMarketOpen(inst.tradingHours) : null;
  const swapCls = (r: string | undefined) => (r && D.from(r).isNeg() ? 'text-down' : 'text-up');

  return (
    <div className="flex flex-1 flex-col">
      <div className="mx-auto w-full max-w-[640px] pb-[calc(env(safe-area-inset-bottom)+112px)]">
        <header
          className={cx(
            'sticky top-0 z-20 flex items-center justify-between gap-3 border-b bg-bg px-4 pt-[calc(env(safe-area-inset-top)+12px)] pb-3 transition-colors',
            scrolled ? 'border-line' : 'border-transparent',
          )}
        >
          <Link to="/markets" aria-label="Back to markets" className={roundCls}>
            <Icon name="left" size={22} strokeWidth={2.2} />
          </Link>
          <button
            type="button"
            onClick={(e) => toggleSearch(e.currentTarget)}
            aria-haspopup="dialog"
            aria-label={`${pairLabel(symbol)}, choose another market`}
            className="inline-flex h-12 min-w-0 items-center gap-2 rounded-full border border-float-line bg-float pr-[18px] pl-[22px] text-[17px] font-semibold text-fg shadow-float"
          >
            <span className="truncate">{pairLabel(symbol)}</span>
            <Icon name="chevron" size={18} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={() => toggleFavorite(symbol)}
            aria-pressed={fav}
            aria-label={fav ? `Remove ${symbol} from favorites` : `Add ${symbol} to favorites`}
            className={cx(roundCls, fav && 'text-warn')}
          >
            <Icon name="star" size={22} filled={fav} />
          </button>
          <button type="button" onClick={() => openAlertBuilder({ symbol })} aria-label={`Set an alert on ${pairLabel(symbol)}`} className={roundCls}>
            <Icon name="alert" size={21} />
          </button>
        </header>
        <TradingStatusBanner className="mx-4 mt-1 rounded-2xl border" />
        <PriceFeedBanner symbol={symbol} className="mx-4 mt-1 rounded-2xl border" />

        <div className="px-5 pt-2.5">
          <div className="flex items-center gap-2.5">
            <InstrumentIcon symbol={symbol} size={28} />
            <span className="truncate text-[17px] font-semibold">{inst?.name ?? symbol}</span>
            {inst && <span className="shrink-0 rounded-[7px] bg-pill px-2 py-0.5 text-[13px] font-semibold text-muted">{inst.maxLeverage}x</span>}
            <span className={cx('ml-auto flex shrink-0 items-center gap-1.5 text-[14px] font-semibold', open ? 'text-up' : 'text-muted')}>
              <i className={cx('size-2 rounded-full', open ? 'bg-up' : 'bg-muted')} />
              {open ? (always ? 'Open 24/7' : 'Open') : opensAt ? `Opens ${fmtOpens(opensAt)}` : 'Closed'}
            </span>
          </div>
          <div className="tnum mt-2.5 text-[44px] leading-[52px] font-semibold tracking-[-0.03em]">{quote ? fmtPrice(quote.mark, dp, ccy) : '—'}</div>
          {move && (
            <div className="mt-1 flex items-center gap-2 text-[17px] leading-6" title={move.title}>
              <MoveArrow fall={isFall(move.pct)} size={20} className={isFall(move.pct) ? 'text-down' : 'text-up'} />
              <span className={cx('tnum font-semibold', isFall(move.pct) ? 'text-down' : 'text-up')}>
                {ccy === 'USD' ? fmtUsdSigned(move.abs.toFixed(dp), dp) : fmtSigned(move.abs.toFixed(dp), dp)} ({fmtPct(move.pct.toFixed(2))})
              </span>
              <span className="text-muted">{move.label}</span>
            </div>
          )}
        </div>

        <div className="mt-2.5">
          <ChartPanel symbol={symbol} compact />
        </div>

        {positions.length > 0 && (
          <div className="mx-4 mt-[18px] flex flex-col gap-3">
            <MarginWarning />
            {positions.map((p) => (
              <PositionCard key={p.id} p={p} titled />
            ))}
          </div>
        )}

        <div className="mt-6 flex items-baseline justify-between gap-3 px-5">
          <h2 className="m-0 text-[20px] font-semibold tracking-[-0.01em]">Market stats</h2>
          <Link to={`/markets/${symbol}`} className="text-[15px] font-semibold text-accent-fg no-underline">
            About {pairLabel(symbol)} →
          </Link>
        </div>
        <dl className="m-0 mt-3 grid grid-cols-2 gap-x-4 gap-y-3.5 px-5">
          <Stat label="Bid" className="text-up">
            {quote ? fmtPrice(quote.bid, dp, ccy) : '—'}
          </Stat>
          <Stat label="Ask" className="text-down">
            {quote ? fmtPrice(quote.ask, dp, ccy) : '—'}
          </Stat>
          {showsVolume(inst?.assetClass) && <Stat label="24h volume">{ticker ? fmtCompactUsd(ticker.vol24) : '—'}</Stat>}
          <Stat label="Open interest">{ticker ? fmtCompactUsd(ticker.oi) : '—'}</Stat>
          {market?.volume24h != null && <Stat label="24h volume, all exchanges">{fmtCompactUsd(String(Math.round(market.volume24h)))}</Stat>}
          {market?.marketCap != null && <Stat label={market.rank ? `Market cap · #${market.rank}` : 'Market cap'}>{fmtCompactUsd(String(Math.round(market.marketCap)))}</Stat>}
          <Stat label="Swap buy" className={swapCls(inst?.swapLongRate)}>
            {inst ? `${D.from(inst.swapLongRate).toFixed(4)}%` : '—'}
          </Stat>
          <Stat label="Swap sell" className={swapCls(inst?.swapShortRate)}>
            {inst ? `${D.from(inst.swapShortRate).toFixed(4)}%` : '—'}
          </Stat>
          <Stat label="1 lot">{inst ? `${fmt(inst.lotSize, 0)} ${unitLabel(inst.unit, inst.lotSize)}` : '—'}</Stat>
        </dl>

      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-bg">
        <div className="mx-auto grid max-w-[640px] grid-cols-2 gap-2.5 px-4 pt-3.5 pb-[max(20px,env(safe-area-inset-bottom))]">
          {(['long', 'short'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setTicket(s)}
              className={cx('flex h-[58px] flex-col items-center justify-center rounded-full', s === 'long' ? 'bg-btn-long text-btn-long-fg' : 'bg-btn-short text-btn-short-fg')}
            >
              <span className="text-[17px] leading-[22px] font-semibold">{sideLabel(s)}</span>
              <span className="tnum text-[12.5px] leading-4">
                {s === 'long' ? 'Ask' : 'Bid'} {quote ? fmtPrice(s === 'long' ? quote.ask : quote.bid, dp, ccy) : '—'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {toast && (
        <div role="status" className="sheet-in fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+112px)] z-40 mx-auto flex max-w-[480px] items-center gap-2.5 rounded-2xl bg-fg px-4 py-3 text-[15px] font-semibold text-bg shadow-float">
          <Icon name="check" size={18} strokeWidth={2.6} />
          {toast}
        </div>
      )}
      {ticket && <OrderTicket symbol={symbol} initialSide={ticket} onClose={closeTicket} onDone={(m) => (setTicket(null), setToast(m))} />}
    </div>
  );
}
